import { useCallback, useEffect, useRef, useState } from 'react';

import { testCustomModel } from '@/api/registry';
import { buildPreviewPayload } from '../payload';

/**
 * Runs `/test/` and owns the result panel's state.
 *
 * `result` is null until a test has been run, and goes back to null on any
 * edit — the panel describes a specific version of the form, so it has to
 * disappear rather than linger once that version is gone.
 *
 * Each run captures the generation it was issued under. Only one test can be
 * in flight (the button is disabled while running), so the only race is the
 * form changing under a slow response; a response from a superseded
 * generation is dropped rather than repopulating a panel the edit just
 * cleared.
 */
export const useModuleTest = ({ getValues, inputs, outputs, params, balance }) => {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    setResult(null);
  }, [inputs, outputs, params, balance]);

  const run = useCallback(async () => {
    const data = getValues();
    const generation = generationRef.current;
    setRunning(true);
    try {
      const res = await testCustomModel(buildPreviewPayload(data, { label: data.label }));
      if (generationRef.current === generation) setResult(res.data);
    } catch (err) {
      const msg = err?.response?.data?.error ?? err.message ?? 'Test run failed';
      if (generationRef.current === generation) setResult({ error: msg });
    } finally {
      setRunning(false);
    }
  }, [getValues]);

  const clear = useCallback(() => setResult(null), []);

  return { result, running, run, clear, setResult };
};
